import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  GetAllGroupsResult,
  GroupResult,
} from "../../../../services/api/types/portalGroup/portalGroup";
import { error } from "../../../../theme/themePrimitives";

interface ReplaceGroupModalProps {
  openReplaceGroupModal: boolean;
  setOpenReplaceGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  confirmButtonClick: () => void;
  handleModalConfirm?: () => void;
  modalLoading?: boolean;
  fetchPortalGroups: GetAllGroupsResult[];
  setParentPortalGroupID: React.Dispatch<React.SetStateAction<string | null>>;
  replacePortalDropdownNameError: boolean;
  setReplacePortalDropdownNameError: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const ReplaceGroupModal: React.FC<ReplaceGroupModalProps> = ({
  openReplaceGroupModal,
  setOpenReplaceGroupModal,
  modalTitle,
  confirmButtonClick,
  handleModalConfirm,
  modalLoading,
  fetchPortalGroups,
  setParentPortalGroupID,
  replacePortalDropdownNameError,
  setReplacePortalDropdownNameError,
}) => {
  const [portalName, setPortalName] = useState("");
  const [portalId, setPortalId] = useState("");

  const handlePortalNameChange = (event: any, child: any) => {
    const selectedName = event.target.value;
    const selectedId = (child?.props as any)["data-portalid"];
    setPortalName(selectedName);
    setPortalId(selectedId);
  };

  useEffect(() => {
    setParentPortalGroupID(portalId);
    if (portalId) {
      setReplacePortalDropdownNameError(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId]);

  return (
    <Modal
      open={openReplaceGroupModal}
      onClose={() => setOpenReplaceGroupModal(false)}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          width: "50%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
          }}
        >
          <Typography variant="h5">{modalTitle}</Typography>

          <IconButton
            sx={{
              border: "none",
            }}
            onClick={() => setOpenReplaceGroupModal(false)}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>

        <Divider />
        <Box sx={{ padding: "16px" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Group status
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This group is already in use. Choose which group you will replace
            this group with:
          </Typography>
          <Stack sx={{ marginTop: "16px" }}>
            <Typography variant="subtitle2" sx={{ marginBottom: "5px" }}>
              Select replacement group
            </Typography>
            <Select
              id="replace-group-portal-select"
              size="small"
              value={portalName}
              onChange={(e, child) => {
                handlePortalNameChange(e, child);
              }}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem disabled value="">
                Select a group
              </MenuItem>
              {fetchPortalGroups.map((group: GetAllGroupsResult) => [
                <MenuItem
                  key={group.PortalGroupUid}
                  value={group.name}
                  data-portalid={group.PortalGroupUid}
                >
                  {group.name}
                </MenuItem>,
                ...group.subGroups.map((subgroup: GroupResult) => (
                  <MenuItem
                    key={subgroup.PortalGroupUid}
                    value={subgroup.name}
                    data-portalid={subgroup.PortalGroupUid}
                    sx={{ pl: 2 }}
                  >
                    {subgroup.name}
                  </MenuItem>
                )),
              ])}
            </Select>
            {replacePortalDropdownNameError && (
              <Typography color="error" fontSize="small">
                Please select a group.
              </Typography>
            )}
          </Stack>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", padding: "16px" }}
        >
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Button
              onClick={() => setOpenReplaceGroupModal(false)}
              sx={{
                px: 2,
                border: "1px solid #D0D5DD",
                borderRadius: "8px",
                backgroundColor: `white !important`,
                color: "black",
              }}
            >
              <Typography>Cancel</Typography>
            </Button>
            {modalLoading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={async () => {
                  if (handleModalConfirm) {
                    await handleModalConfirm();
                  }
                  confirmButtonClick();
                }}
                sx={{
                  px: 2,
                  borderRadius: "8px",
                  backgroundColor: `${error[600]} !important`,
                  color: "white",
                }}
              >
                <Typography>Replace and remove</Typography>
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
